import React, { useCallback } from "react";

import type { Appointment, PatientDetails } from "@/features/calendar/types/schedule";
import dayjs from "@/lib/helpers/dayjs";
import { getAutomationDataAttr } from "@/utils/automationUtils";

import { getMoreWeeklyAppointments, getTodaysDate, hasDataForNextWeek } from "./helpers";
import { NavigationButton } from "./NavigationButton";

type WeekNavigationProps = {
  appointments: Appointment[];
  currentWeekStart: Date;
  disableNextArrow?: boolean;
  patientDetails: PatientDetails;
  setAppointments: React.Dispatch<React.SetStateAction<Appointment[]>>;
  setCurrentWeekStart: React.Dispatch<React.SetStateAction<Date>>;
};

export const WeekNavigation = ({
  appointments,
  currentWeekStart,
  disableNextArrow,
  patientDetails,
  setAppointments,
  setCurrentWeekStart,
}: WeekNavigationProps) => {
  const startDateText = dayjs(currentWeekStart).format("MMM DD");
  const startDateTime = currentWeekStart.toISOString();

  const endDate = dayjs(currentWeekStart).add(6, "days");
  const endDateText = endDate.format("MMM DD, YYYY");
  const endDateTime = endDate.toISOString();

  const goToPreviousWeek = useCallback(() => {
    const previousWeek = dayjs(currentWeekStart).subtract(1, "week").toDate();

    setCurrentWeekStart(previousWeek);
  }, [currentWeekStart]);

  const goToNextWeek = useCallback(async () => {
    const nextWeek = dayjs(currentWeekStart).add(1, "week").toDate();

    setCurrentWeekStart(nextWeek);

    const shouldGetMoreWeeklyAppointments = !hasDataForNextWeek(appointments, nextWeek);

    if (shouldGetMoreWeeklyAppointments) {
      const updatedAppointments = await getMoreWeeklyAppointments(
        appointments,
        patientDetails,
        nextWeek,
      );

      setAppointments(updatedAppointments);
    }
  }, [currentWeekStart, appointments, patientDetails]);

  const currentWeekStartOfWeek = getTodaysDate().startOf("isoWeek");
  const isCurrentWeek = dayjs(currentWeekStart).isSame(currentWeekStartOfWeek, "week");

  const canGoBack = !isCurrentWeek;
  const canGoForward = !disableNextArrow;

  return (
    <div className="tw:flex tw:items-center tw:gap-4">
      <NavigationButton
        direction="left"
        isDisabled={!canGoBack}
        onClick={goToPreviousWeek}
        {...getAutomationDataAttr("appointment-time-picker-prev-week")}
      />

      <div
        className="body-md tw:flex tw:gap-2 tw:leading-5 tw:font-bold tw:whitespace-nowrap tw:text-text-primary"
        {...getAutomationDataAttr("appointment-time-picker-week-range")}
      >
        <time dateTime={startDateTime}>{startDateText}</time>–
        <time dateTime={endDateTime}>{endDateText}</time>
      </div>

      <NavigationButton
        direction="right"
        isDisabled={!canGoForward}
        onClick={goToNextWeek}
        {...getAutomationDataAttr("appointment-time-picker-next-week")}
      />
    </div>
  );
};
